import { Status } from "@/types/Status"
import { IReading } from "../models/meterReading/meterReading.types"
import {
  NORMAL_SUPPLY_STATUSES,
  CAUTION_SUPPLY_STATUSES,
  STATUS_DISPLAY_NAMES,
  METER_TYPES,
} from "@/constants/meterpoints"
import dayjs, { Dayjs } from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { IMeter } from "@/models/meter/meter.types"
import { METER_TYPE, MeterType } from "@/constants/imbp"
dayjs.extend(utc)
dayjs.extend(timezone)

export const getSupplyStatus = (statusText: string): Status => {
  switch (true) {
    case NORMAL_SUPPLY_STATUSES.includes(statusText):
      return new Status(
        "normal",
        STATUS_DISPLAY_NAMES[statusText] ?? "On Supply",
      )
    case CAUTION_SUPPLY_STATUSES.includes(statusText):
      return new Status(
        "caution",
        STATUS_DISPLAY_NAMES[statusText] ?? "Registration",
      )
    default:
      return new Status(
        "critical",
        STATUS_DISPLAY_NAMES[statusText] ?? "Not Supplied",
      )
  }
}

export const getTradMeterSupplyStatus = (statusText: string): Status => {
  if (
    NORMAL_SUPPLY_STATUSES.includes(statusText) ||
    CAUTION_SUPPLY_STATUSES.includes(statusText)
  ) {
    return new Status("neutral", "Trad Meter")
  }

  return new Status("default", "Not Supplied")
}

export const getDccStatus = (dcc?: string): Status => {
  return dcc?.toLowerCase() === "active"
    ? new Status("normal", "Active")
    : new Status("critical", "Inactive")
}

export const getDccStatusWithLabel = (dcc?: string): Status => {
  return dcc?.toLowerCase() === "active"
    ? new Status("normal", "DCC active")
    : new Status("critical", "DCC inactive")
}

export const getLatestMeterReadings = (
  meters: IMeter[],
  isSmart = false,
): IReading[] => {
  return meters.flatMap((meter) =>
    meter.registers
      .map((register) => register.getLatestReading({ isSmart }))
      .filter((reading): reading is IReading => reading != null),
  )
}

export const getLatestReadStatus = (meters: IMeter[]): Status => {
  const latestReadings = getLatestMeterReadings(meters)
  if (latestReadings.length) {
    const sortedReadings = [...latestReadings].sort((a, b) => {
      return a.date.isAfter(b.date) ? 1 : -1
    })
    const oldestRead = sortedReadings[0]
    return oldestRead.dateStatus
  } else {
    return new Status("critical", "N/A")
  }
}

export const getLatestSmartReadStatus = (meters: IMeter[]): Status => {
  const latestReadings = getLatestMeterReadings(meters, true)
  if (latestReadings.length) {
    const sortedReadings = [...latestReadings].sort((a, b) => {
      return a.date.isAfter(b.date) ? 1 : -1
    })
    const oldestRead = sortedReadings[0]
    return oldestRead.dateStatus
  } else {
    return new Status("critical", "N/A")
  }
}

export const isSmartMeter = (meterType: string) => {
  if (!meterType) return false

  const matchingMeter = METER_TYPES.find(
    (meterSpec) => meterSpec.typeCode === meterType,
  )
  return matchingMeter ? matchingMeter.isSmartMeter : false
}

export const isSmet2 = (meterType: string) => {
  if (!meterType) return false

  const matchingMeter = METER_TYPES.find(
    (meterSpec) => meterSpec.typeCode === meterType,
  )
  return matchingMeter ? matchingMeter.smetsVersion === 2 : false
}

export const isUnknownMeterType = (meterType: string) => {
  return !meterType || meterType === "UNKNOWN" || meterType === "U"
}

export const isIndustrialMeter = (msn: string) => {
  return msn.includes("M016") || msn.includes("M025")
}

// The old meterpointDetails parser pre-computed a METER_TYPE kind per meter;
// the backoffice structure response carries the raw type code instead, so
// consumers that key off METER_TYPE (e.g. SMEX job notes) derive it here.
export const getMeterTypeKind = (meter: IMeter): MeterType => {
  if (meter.isUnknownMeter) return METER_TYPE.UNKNOWN
  if (!meter.isSmartMeter) return METER_TYPE.TRAD
  return meter.isSmet2 ? METER_TYPE.SMETS2 : METER_TYPE.SMETS1
}

export const isWithinCancellationPeriod = (startDate: Dayjs) => {
  const currentTime = dayjs().tz("Europe/London")
  const supplyStartDate = startDate
    .tz("Europe/London")
    .set("hour", 17)
    .subtract(1, "day")
  return currentTime.isBefore(supplyStartDate)
}

export const getUnit = (isElectricity: boolean) => {
  return isElectricity ? "kWh" : "m\u00B3"
}
